import type { GridColDef } from '@mui/x-data-grid-pro';
import type { GridColumnVisibilityModel, GridSortModel } from '@mui/x-data-grid';
import { SampleSearchKind } from '@/api/ApiClient';
import type { SampleListMode } from '@/lib/sampleList/sampleListMode';

/**
 * Sample grid columns tied to synthesis / growth. Omitted entirely on the Substrates inventory grid.
 */
export const SAMPLE_GRID_GROWTH_COLUMN_FIELDS = [
  'parentLabel',
  'material',
  'grownBy',
  'synthesisTechniquesNames',
  'synthesisInstrumentsNames',
  'growthDates',
] as const;

export const SAMPLE_GRID_DATA_PACKAGE_FIELD = 'dataPackages';

export type SampleGridGrowthColumnField = (typeof SAMPLE_GRID_GROWTH_COLUMN_FIELDS)[number];

const growthColumnFieldSet = new Set<string>(SAMPLE_GRID_GROWTH_COLUMN_FIELDS);

export function isSampleGridGrowthColumnField(field: string): boolean {
  return growthColumnFieldSet.has(field);
}

/**
 * Data-package column labels the owning package on snapshot search rows (`SampleSearchResultDto.dataPackages`).
 * Omit when the search cannot return snapshots: live-only kind, or the Substrates inventory page (always
 * posts `kind: Active` — live pool for growth picks; packaged substrates are found on Samples search).
 */
export function sampleGridShowsDataPackageColumn(listMode: SampleListMode, kind?: SampleSearchKind | null): boolean {
  if (listMode === 'substrates') return false;
  return (kind ?? SampleSearchKind.Both) !== SampleSearchKind.Active;
}

/** Drops growth columns when `listMode` is `substrates` (not present in grid or column picker). */
export function filterSampleGridColumnsForListMode(columns: GridColDef[], listMode: SampleListMode): GridColDef[] {
  if (listMode !== 'substrates') return columns;
  return columns.filter((col) => !col.field || !isSampleGridGrowthColumnField(col.field));
}

/** Drops the data-package column when the current search cannot return snapshots. */
export function filterSampleGridColumnsForSearchKind(
  columns: GridColDef[],
  listMode: SampleListMode,
  kind?: SampleSearchKind | null
): GridColDef[] {
  if (sampleGridShowsDataPackageColumn(listMode, kind)) return columns;
  return columns.filter((col) => col.field !== SAMPLE_GRID_DATA_PACKAGE_FIELD);
}

/**
 * When the data-package column is omitted (live-only), MUI may drop its visibility key.
 * Keep the stored preference so it still applies after switching back to snapshots.
 */
export function preserveOmittedDataPackageVisibility(
  previous: GridColumnVisibilityModel,
  incoming: GridColumnVisibilityModel,
  columnPresent: boolean
): GridColumnVisibilityModel {
  if (columnPresent) return incoming;
  if (!(SAMPLE_GRID_DATA_PACKAGE_FIELD in previous)) {
    const next = { ...incoming };
    delete next[SAMPLE_GRID_DATA_PACKAGE_FIELD];
    return next;
  }
  return { ...incoming, [SAMPLE_GRID_DATA_PACKAGE_FIELD]: previous[SAMPLE_GRID_DATA_PACKAGE_FIELD] };
}

/** Removes sort items that reference columns not on the substrates grid. */
export function filterSortModelForListMode(sortModel: GridSortModel, listMode: SampleListMode): GridSortModel {
  if (listMode !== 'substrates') return sortModel;
  return sortModel.filter((item) => !isSampleGridGrowthColumnField(item.field));
}

/**
 * Strips growth column keys from a visibility model and merges onto substrate defaults.
 */
export function sanitizeSubstrateColumnVisibilityModel(
  stored: GridColumnVisibilityModel,
  defaults: GridColumnVisibilityModel
): GridColumnVisibilityModel {
  const next: GridColumnVisibilityModel = { ...defaults };
  for (const [key, value] of Object.entries(stored)) {
    if (isSampleGridGrowthColumnField(key)) continue;
    next[key] = value;
  }
  return next;
}
